import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter, Input, HostListener, AfterContentInit } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { UtilService } from "@app-shared/services/util.service";
import { BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";
import { animate, style, transition, trigger } from "@angular/animations";
import { environment } from '../../environments/environment';
import { AccountService } from "@app/account/account.service";
import { TourService } from "@app/services/tour.service";
import { SupportChatService } from "@app-shared/services/support-chat.service";

interface MenuItem {
  icon: string;
  label: string;
  route?: string;
  active?: boolean;
  expanded?: boolean;
}

@Component({
    selector: "app-sidebar",
    templateUrl: "./sidebar.component.html",
    styleUrls: ["./sidebar.component.scss"],
    animations: [
        trigger("slideInOut", [
            transition(":enter", [
                style({ height: 0, opacity: 0 }),
                animate("200ms ease-out", style({ height: "*", opacity: 1 })),
            ]),
            transition(":leave", [
                style({ height: "*", opacity: 1 }),
                animate("200ms ease-in", style({ height: 0, opacity: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class SidebarComponent implements OnInit {
  @Output() sidebarToggled = new EventEmitter<boolean>();

  menuItems: MenuItem[] = [];
  isExpanded = false;
  private _isExpanded = new BehaviorSubject<boolean>(false);
  isExpanded$ = this._isExpanded.asObservable();
  hoveredMenuItem: MenuItem | null = null;
  showHoverSidebox = false;
  hoverTabs: any[] = [];
  sidebarLeftPosition = 0;
  sidebarTopPosition = 0;
  subscribedFeatures: any;
  isAdmin: boolean = false;
  isOrgSuperAdmin: boolean = false;

  private isMouseOverSidebox = false;
  private hoverTimeout: any;

  private menuRoutes: { [key: string]: string } = {
    dashboard: '/Dashboard',
    operations: '/Assessments/AssessmentPlan', // Changed from Operations to Assessments
    library: '/Library/Questions',
    customer: '/Customer/Customer',
    finance: '/Finance/Invoices',
    setup: '/Setup/Products'
  };

  private menuOrder: { [key: string]: number } = {
    dashboard: 1,
    assessments: 2,
    library: 3,
    customer: 4,
    finance: 5,
    setup: 6
  };

  developerUrl: any;

  constructor(
    private router: Router,
    private util: UtilService,
    private cdr: ChangeDetectorRef,
    private accSrvc: AccountService,
    private tourService: TourService,
    private supportChatService: SupportChatService
  ) { }

  ngOnInit() {
    this.developerUrl = `${this.util.getApiUrl()}/docs/?token=${this.accSrvc.jwt}`;
    this.initializeMenuItems();
    this.setActiveRoute(this.router.url);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.setActiveRoute(event.url);
        this.cdr.detectChanges();
      });
    setTimeout(() => {
      this.setActiveRoute(this.router.url);
      this.cdr.detectChanges();
    });

    const ur = this.util.decrypt(localStorage.getItem('ur'));
    this.isOrgSuperAdmin = JSON.parse(localStorage.getItem('isOrgSuperAdmin'));
    this.isAdmin = ur === 'SA';
  }

  private isSystemSuperAdmin(): boolean {
    return localStorage.getItem('isSystemSuperAdmin') === 'true';
  }

  private isSubscriptionPlanEntry(entry: any): boolean {
    const menuName = (entry?.menu_name || entry?.label || '').toLowerCase();
    const route = (entry?.url || entry?.route || '').toLowerCase();
    return menuName.includes('subscription') || route.includes('/admin/subscriptions');
  }

  private pruneSubscriptionPlanEntries(entries: any[] = []): any[] {
    if (this.isSystemSuperAdmin()) {
      return entries;
    }

    return entries
      .filter((entry) => !this.isSubscriptionPlanEntry(entry))
      .map((entry) => {
        const clonedEntry = { ...entry };
        if (Array.isArray(clonedEntry.pages) && clonedEntry.pages.length) {
          clonedEntry.pages = this.pruneSubscriptionPlanEntries(clonedEntry.pages);
        }
        return clonedEntry;
      })
      .filter((entry) => {
        const hasVisiblePages = Array.isArray(entry.pages) ? entry.pages.length > 0 : false;
        return !this.isSubscriptionPlanEntry(entry) && (hasVisiblePages || !!entry.url || !!entry.route || !!entry.id);
      });
  }

  @Input() set expanded(value: boolean) {
    this.isExpanded = value;
    this._isExpanded.next(value);
    this.cdr.detectChanges();
  }

  toggleSidebar() {
    this.isExpanded = !this.isExpanded;
    this._isExpanded.next(this.isExpanded);
    this.sidebarToggled.emit(this.isExpanded);
    this.cdr.detectChanges();
  }

  navigateToNestedTab(tab: any): void {
    if (tab.route) {
      this.router.navigate([tab.route]);
      this.showHelpSidebox = false;
      this.showAboutCertificateMenu = false;
      this.isMouseOverHelpSidebox = false;
      this.isMouseOverNestedMenu = false;
    }
  }

  private shouldShowSidebox(item: MenuItem): boolean {
    const excludedItems = ['home', 'dashboard', 'assessments'];
    const itemLabel = item.label.toLowerCase();
    const itemIcon = item.icon.toLowerCase();
    return !(excludedItems.includes(itemLabel) || excludedItems.includes(itemIcon));
  }
  
  onMenuItemHover(event: MouseEvent, item: MenuItem): void {
    if (!this.shouldShowSidebox(item)) {
      this.showHoverSidebox = false;
      this.hoveredMenuItem = null;
      return;
    }

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    this.hoveredMenuItem = item;
    this.showHelpSidebox = false;
    this.showAboutCertificateMenu = false;
    this.showHoverSidebox = true;
    this.loadHoverTabs(item);
    this.calculateSideboxPosition(event, item);
    this.cdr.detectChanges();
  }

  onMenuItemLeave(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToSidebox = relatedTarget?.closest('.hover-sidebox');
    const isGoingToSameMenuItem = relatedTarget?.closest('.menu-item');

    if (!isGoingToSidebox && !isGoingToSameMenuItem) {
      this.hoverTimeout = setTimeout(() => {
        if (!this.isMouseOverSidebox) {
          this.showHoverSidebox = false;
          this.hoveredMenuItem = null;
          this.cdr.detectChanges();
        }
      }, 100);
    }
  }
  
  // Add global mouse move listener to detect when cursor leaves completely
  @HostListener('document:mousemove', ['$event'])
  onGlobalMouseMove(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isOverRelevantElement = target.closest('.sidebar') ||
      target.closest('.hover-sidebox') ||
      target.closest('.help-sidebox') ||
      target.closest('.nested-sidebox');

    if (!isOverRelevantElement && (this.showHoverSidebox || this.showHelpSidebox || this.showAboutCertificateMenu)) {
      this.scheduleCloseAll();
    }
  }
  
  // Clean up timeouts
  ngOnDestroy(): void {
    if (this.closeAllTimeout) {
      clearTimeout(this.closeAllTimeout);
    }
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.helpHoverTimeout) {
      clearTimeout(this.helpHoverTimeout);
    }
  }

  private scheduleCloseAll(): void {
    if (this.closeAllTimeout) {
      clearTimeout(this.closeAllTimeout);
    }

    this.closeAllTimeout = setTimeout(() => {
      if (!this.isMouseOverAnyElement()) {
        this.closeAllSideboxes();
      }
    }, 150);
  }
  
  private isMouseOverAnyElement(): boolean {
    return this.isMouseOverSidebox ||
      this.isMouseOverHelpSidebox ||
      this.isMouseOverNestedMenu ||
      this.isMouseOverSidebar();
  }

  private isMouseOverSidebar(): boolean {
    const sidebar = document.querySelector('.sidebar');
    return sidebar ? sidebar.matches(':hover') : false;
  }
  
  onSideboxMouseEnter(): void {
    this.isMouseOverSidebox = true;
    this.cancelCloseAll();
  }

  onSideboxMouseLeave(event: MouseEvent): void {
    this.isMouseOverSidebox = false;
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToSidebar = relatedTarget?.closest('.sidebar');
    if (!isGoingToSidebar) {
      this.hoverTimeout = setTimeout(() => {
        this.showHoverSidebox = false;
        this.hoveredMenuItem = null;
        this.cdr.detectChanges();
      }, 150);
    }
  }

  onSideboxTabHover(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  }

  private loadHoverTabs(menuItem: MenuItem): void {
    this.hoverTabs = [];
    
    // Don't load hover tabs for Assessments
    if (menuItem.label.toLowerCase() === 'assessments') {
      return;
    }
    
    const encryptedMenus = localStorage.getItem("menu_access");
    if (!encryptedMenus) return;
    const menus = this.pruneSubscriptionPlanEntries(JSON.parse(this.util.decrypt(encryptedMenus)));
    const menuName = menuItem.label.toLowerCase();
    const foundMenu = this.findMenuByName(menus, menuName);
    if (foundMenu && foundMenu.pages) {
      this.hoverTabs = this.processMenuPages(foundMenu.pages);
      const excludedKeywords = ['candidate', 'candidates', 'rate master', 'ratemaster'];
      this.hoverTabs = this.hoverTabs.filter(tab =>
        !excludedKeywords.some(keyword => tab.label.toLowerCase().includes(keyword))
      );
    }
  }

  private findMenuByName(menus: any[], menuName: string): any {
    for (const menu of menus) {
      const currentMenuName = menu.menu_name?.toLowerCase();
      const formattedMenuName = menuName === 'customers' ? 'customer' : menuName;
      if (currentMenuName === formattedMenuName) {
        return menu;
      }
      if (menu.pages) {
        const found = this.findMenuByName(menu.pages, menuName);
        if (found) return found;
      }
    }
    return null;
  }

  private processMenuPages(pages: any[]): any[] {
    const processedPages = pages
      .filter(page => page.menu_name && page.url)
      .map(page => ({
        label: this.getModifiedLabel(page.menu_name),
        route: page.url,
        icon: this.getMenuIcon(page.menu_name),
        active: this.router.url.startsWith(page.url)
      }));
    return this.sortLibraryPages(processedPages);
  }

  private sortLibraryPages(pages: any[]): any[] {
    const customOrder: { [key: string]: number } = {
      'questions': 1,
      'question banks': 2,
      'question papers': 3,
      'question paper': 3
    };
    return pages.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      const aOrder = customOrder[aLabel] || 999;
      const bOrder = customOrder[bLabel] || 999;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.label.localeCompare(b.label);
    });
  }

  navigateToTab(tab: any): void {
    if (tab.route) {
      this.router.navigate([tab.route]);
      this.showHoverSidebox = false;
      this.hoveredMenuItem = null;
      this.isMouseOverSidebox = false;
    }
  }

  navigateToRoute(route: string): void {
    if (route) {
      this.router.navigate([route]);
    }
  }

  private isMouseOverNestedMenu = false;
  nestedMenuLeftPosition = 0;
  nestedMenuTopPosition = 0;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const sidebar = document.querySelector('.sidebar');
    const sidebox = document.querySelector('.hover-sidebox');
    const helpSidebox = document.querySelector('.help-sidebox');
    const helpIcon = document.querySelector('.help-menu-item');
    const nestedMenu = document.querySelector('.nested-sidebox');

    if (sidebar && !sidebar.contains(event.target as Node) &&
      sidebox && !sidebox.contains(event.target as Node) &&
      helpSidebox && !helpSidebox.contains(event.target as Node) &&
      helpIcon && !helpIcon.contains(event.target as Node) &&
      nestedMenu && !nestedMenu.contains(event.target as Node)) {
      this.showHoverSidebox = false;
      this.hoveredMenuItem = null;
      this.showHelpSidebox = false;
      this.showAboutCertificateMenu = false;
      this.cdr.detectChanges();
    }
  }

  private buildDynamicMenuRoutes(newMenus: { menu_name: string; urls: string[] }[]): void {
    const defaultMenuRoutes = {
      dashboard: '/Dashboard',
      operations: '/Assessments/AssessmentPlan',
      library: '/Library/Questions',
      customer: '/Customer/Customer',
      finance: '/Finance/Invoices',
      setup: '/Setup/Products'
    };
    this.menuRoutes = { ...defaultMenuRoutes };
    newMenus.forEach(menu => {
      const menuName = menu.menu_name.toLowerCase();
      const availableUrls = menu.urls.filter(url => this.hasAccessToUrl(url));
      if (availableUrls.length > 0) {
        if (!this.hasAccessToUrl(defaultMenuRoutes[menuName])) {
          this.menuRoutes[menuName] = availableUrls[0];
        }
      }
    });
  }

  private hasAccessToUrl(url: string): boolean {
    const encryptedMenus = localStorage.getItem("menu_access");
    if (!encryptedMenus) return false;
    const menus = JSON.parse(this.util.decrypt(encryptedMenus));
    return menus.some(menu => {
      return (
        menu.url === url ||
        (menu.pages && menu.pages.some(page => page.url === url))
      );
    });
  }

  private initializeMenuItems(): void {
    const encryptedMenus = localStorage.getItem("menu_access");
    if (encryptedMenus) {
      const menus = this.pruneSubscriptionPlanEntries(JSON.parse(this.util.decrypt(encryptedMenus)));
      this.processMenus(menus);
      const newMenus = this.getMenuNamesWithUrls(menus);
      this.buildDynamicMenuRoutes(newMenus);

      this.menuItems.forEach(item => {
        const menuName = item.label.toLowerCase();
        if (menuName === 'operations' || menuName === 'assessments') {
          item.route = '/Assessments/AssessmentPlan';
        }
      });

      if (!this.menuItems.some(item => item.route?.includes('Dashboard'))) {
        this.menuItems.unshift({
          icon: 'home',
          label: 'Home',
          route: '/Dashboard',
          active: false
        });
      }
    }
  }

  private getMenuNamesWithUrls(menus: any[]): { menu_name: string; urls: string[] }[] {
    return menus
      .filter(menu => menu.menu_name.toLowerCase() !== "usersettings")
      .map(menu => {
        const urls: string[] = [];
        if (menu.url) {
          urls.push(menu.url);
        }
        if (menu.pages && Array.isArray(menu.pages)) {
          menu.pages.forEach(page => {
            if (page.url) {
              urls.push(page.url);
            }
          });
        }
        return {
          menu_name: menu.menu_name,
          urls
        };
      });
  }

  private processMenus(menus: any[]): void {
    this.menuItems = menus
      .filter((menu) => menu.menu_name.toLowerCase() !== "usersettings")
      .sort((a, b) => {
        const aName = a.menu_name.toLowerCase();
        const bName = b.menu_name.toLowerCase();
        const aOrder = this.menuOrder[aName] || Infinity;
        const bOrder = this.menuOrder[bName] || Infinity;
        return aOrder - bOrder;
      })
      .map((menu) => this.mapToMenuItem(menu));
  }

  private mapToMenuItem(menu: any): MenuItem {
    const menuName = menu.menu_name.toLowerCase();
    let route = this.menuRoutes[menuName] || menu.url;

    // Special case for operations/assessments
    if (menuName === 'operations' || menuName === 'assessments') {
      route = '/Assessments/AssessmentPlan';
    }
    return {
      icon: this.getMenuIcon(menu.menu_name),
      label: this.getMenuLabelName(menu),
      route: route,
      expanded: false,
    };
  }

  helpTabs: any[] = [
    { label: 'Tickets', route: '/tickets', icon: 'ticket-alt' },
    { label: 'Tutorials', route: '/tutorials', icon: 'play-circle' },
    { label: 'API Documentation ', action: 'openApiDocs', icon: 'laptop' },
    {
      label: 'About Certiplate',
      route: '/help/about-certificate',
      icon: 'info-circle',
      hasNestedMenu: true
    },
    { label: 'Product Guide Tour', action: 'startTour', icon: 'compass' }
  ];

  helpMenuItem: MenuItem = {
    icon: 'question-circle',
    label: 'Help',
    active: false
  };

  showHelpSidebox = false;
  helpSideboxLeftPosition = 0;
  helpSideboxTopPosition = 0;
  private isMouseOverHelpSidebox = false;
  private helpHoverTimeout: any;

  get visibleHelpTabs(): any[] {
    return this.helpTabs;
  }

  onHelpIconHover(event: MouseEvent): void {
    if (this.helpHoverTimeout) {
      clearTimeout(this.helpHoverTimeout);
    }
    this.showHelpSidebox = true;
    this.calculateHelpSideboxPosition(event);
    this.cdr.detectChanges();
  }

  onHelpIconLeave(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToSidebox = relatedTarget?.closest('.help-sidebox');
    if (!isGoingToSidebox) {
      this.helpHoverTimeout = setTimeout(() => {
        if (!this.isMouseOverHelpSidebox) {
          this.showHelpSidebox = false;
          this.cdr.detectChanges();
        }
      }, 100);
    }
  }
  
  onHelpSideboxMouseEnter(): void {
    this.isMouseOverHelpSidebox = true;
    this.cancelCloseAll();
  }

  showAboutCertificateMenu = false;
  aboutCertificateTabs: any[] = [
    { label: `Version v3.${environment.version}`, route: '/releasenotes', icon: 'tag' },
    { label: 'Release History', route: '/releasehistory', icon: 'history' }
  ];

  onHelpSideboxMouseLeave(event: MouseEvent): void {
    this.isMouseOverHelpSidebox = false;
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToSidebar = relatedTarget?.closest('.sidebar');
    const isGoingToNestedMenu = relatedTarget?.closest('.nested-sidebox');
    const isGoingToMainSidebox = relatedTarget?.closest('.hover-sidebox');

    if (!isGoingToSidebar && !isGoingToNestedMenu && !isGoingToMainSidebox) {
      this.scheduleCloseAll();
    }
  }

  onAboutCertificateHover(event: MouseEvent, tab: any): void {
    if (tab.hasNestedMenu) {
      this.showAboutCertificateMenu = true;
      this.calculateNestedMenuPosition(event);
      this.cdr.detectChanges();
    }
  }

  onAboutCertificateLeave(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToNestedMenu = relatedTarget?.closest('.nested-sidebox');
    if (!isGoingToNestedMenu) {
      setTimeout(() => {
        if (!this.isMouseOverNestedMenu) {
          this.showAboutCertificateMenu = false;
          this.cdr.detectChanges();
        }
      }, 100);
    }
  }

  onNestedMenuMouseLeave(event: MouseEvent): void {
    this.isMouseOverNestedMenu = false;
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isGoingToParent = relatedTarget?.closest('.help-sidebox');
    const isGoingToSidebar = relatedTarget?.closest('.sidebar');
    const isGoingToMainSidebox = relatedTarget?.closest('.hover-sidebox');

    if (!isGoingToParent && !isGoingToSidebar && !isGoingToMainSidebox) {
      this.scheduleCloseAll();
    }
  }

  private calculateSideboxPosition(event: MouseEvent, item: MenuItem): void {
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) return;

    const sidebarRect = sidebar.getBoundingClientRect();
    const iconElement = event.target as HTMLElement;
    const iconRect = iconElement.getBoundingClientRect();

    // Calculate the center position of the icon
    const iconCenterY = iconRect.top + (iconRect.height / 2);

    this.sidebarLeftPosition = sidebarRect.right + 10;

    // Position the sidebox so it's centered with the icon
    // We subtract half of the estimated sidebox height to center it
    const estimatedSideboxHeight = Math.min(300, this.hoverTabs.length * 50 + 80);
    this.sidebarTopPosition = iconCenterY - (estimatedSideboxHeight / 2);

    // Ensure the sidebox stays within viewport bounds
    const viewportHeight = window.innerHeight;
    const minTop = 20;
    const maxTop = viewportHeight - estimatedSideboxHeight - 20;
    this.sidebarTopPosition = Math.max(minTop, Math.min(this.sidebarTopPosition, maxTop));
  }
  
  private calculateHelpSideboxPosition(event: MouseEvent): void {
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) return;
    const sidebarRect = sidebar.getBoundingClientRect();
    const helpIcon = document.querySelector('.help-menu-item');
    if (!helpIcon) return;
    const helpIconRect = helpIcon.getBoundingClientRect();
    const helpIconCenterY = helpIconRect.top + (helpIconRect.height / 2);

    this.helpSideboxLeftPosition = sidebarRect.right + 12;
    const estimatedSideboxHeight = Math.min(320, this.helpTabs.length * 44 + 32);
    this.helpSideboxTopPosition = helpIconCenterY - (estimatedSideboxHeight * 0.82);
    
    const viewportHeight = window.innerHeight;
    const minTop = 16;
    const maxTop = viewportHeight - estimatedSideboxHeight - 16;
    this.helpSideboxTopPosition = Math.max(minTop, Math.min(this.helpSideboxTopPosition, maxTop));
  }
  
  private closeAllTimeout: any;
  private isMouseOverAnySidebox = false;

  // Add this method for overlay
  onOverlayMouseEnter(): void {
    this.closeAllSideboxes();
  }

  private calculateNestedMenuPosition(event: MouseEvent): void {
    const helpSidebox = document.querySelector('.help-sidebox');
    if (!helpSidebox) return;
    const helpSideboxRect = helpSidebox.getBoundingClientRect();
    const aboutCertificateTab = (event.target as HTMLElement)?.closest('.sidebox-tab-item') as HTMLElement || event.target as HTMLElement;
    const tabRect = aboutCertificateTab ? aboutCertificateTab.getBoundingClientRect() : null;
    
    this.nestedMenuLeftPosition = helpSideboxRect.right + 8;
    const tabTop = tabRect ? tabRect.top : (helpSideboxRect.bottom - 80);
    const estimatedNestedHeight = Math.min(200, this.aboutCertificateTabs.length * 44 + 32);
    
    this.nestedMenuTopPosition = tabTop - 15;
    const viewportHeight = window.innerHeight;
    const minTop = 16;
    const maxTop = viewportHeight - estimatedNestedHeight - 16;
    this.nestedMenuTopPosition = Math.max(minTop, Math.min(this.nestedMenuTopPosition, maxTop));
  }

  navigateToHelpTab(tab: any): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (tab.hasNestedMenu) {
      return;
    }
    if (tab.action === 'openApiDocs') {
      this.openApiDocumentation();
    } else if (tab.action === 'startTour') {
      this.tourService.startDashboardTour();
    } else if (tab.route) {
      this.router.navigate([tab.route]);
    }
    setTimeout(() => {
      this.closeAllSideboxes();
    }, 100);
  }

  private openApiDocumentation(): void {
    const apiUrl = `${this.util.getApiUrl()}/docs/?token=${this.accSrvc.jwt}`;
    window.open(apiUrl, '_blank');
  }
  
  onNestedMenuMouseEnter(): void {
    this.isMouseOverNestedMenu = true;
    this.cancelCloseAll();
  }

  // Cancel any pending close operations
  private cancelCloseAll(): void {
    if (this.closeAllTimeout) {
      clearTimeout(this.closeAllTimeout);
    }
  }
  
  // Modify closeAllSideboxes method
  private closeAllSideboxes(): void {
    this.showHoverSidebox = false;
    this.showHelpSidebox = false;
    this.showAboutCertificateMenu = false;
    this.hoveredMenuItem = null;
    this.isMouseOverSidebox = false;
    this.isMouseOverHelpSidebox = false;
    this.isMouseOverNestedMenu = false;
    this.cdr.detectChanges();
  }

  private getMenuIcon(menuName: string): string {
    const icons: { [key: string]: string } = {
      dashboard: "home",
      Assessments: "cogs",
      library: "book",
      resources: "users",
      assessments: "clipboard-list",
      assessmentsplan: "tasks",
      candidates: "user-graduate",
      projects: "project-diagram",
      Reports: "tasks",
      plan: "calendar",
      "conduct viva": "video",
      invigilate: "eye",
      monitor: "chart-line",
      evaluate: "check-square",
      roles: "user-tag",
      "user category": "user-cog",
      users: "users",
      customer: "user-tie",
      finance: "dollar-sign",
      setup: "cog",
      "activity tracker": "chart-line",
      questions: "file-alt",
      "question paper": "file-alt",
      "question papers": "file-pen",
      accounts: "user-tie",
      leads: "project-diagram",
      invoices: "receipt",
      bills: "file-contract",
    };
    return icons[menuName.toLowerCase()] || "circle";
  }

  private getMenuLabelName(menu: any): string {
    const name = menu.menu_name.toLowerCase();
    if (name === "dashboard") return "Home";
    if (name === "customer") return "Customers";
    if (name === "report") return "Reports";
    if (name === "project") return "Projects";
    if (name === "operations") return "Assessments";
    return menu.menu_name;
  }

  private setActiveRoute(url: string): void {
    if (!url) return;
    const normalizedUrl = url.split('?')[0];
    const cleanUrl = normalizedUrl.endsWith('/') ? normalizedUrl.slice(0, -1) : normalizedUrl;
    const topLevelSegment = '/' + cleanUrl.split('/')[1];
    this.menuItems.forEach((item) => {
      if (!item.route) return;
      const itemRoute = item.route.endsWith('/') ? item.route.slice(0, -1) : item.route;
      const itemBaseRoute = '/' + itemRoute.split('/')[1];
      if (itemRoute === '/Dashboard') {
        item.active = cleanUrl === '/Dashboard' || cleanUrl === '' || cleanUrl === '/';
      } else {
        item.active = itemBaseRoute === topLevelSegment;
      }
    });
    this.cdr.detectChanges();
  }

  private getModifiedLabel(label: string): string {
    const lowerLabel = label?.toLowerCase();
    const labelMap: { [key: string]: string } = {
      'report': 'Reports',
      'customer': 'Customers',
      'project': 'Projects',
      'lead': 'Leads',
      'scheme': 'Schemes',
      'customer group': 'Customer Groups',
      'assessments': 'Assessments',
      'assessmentsplan': 'Assessment',
      'candidates': 'Candidates',
      'questionbank': 'Question Banks',
      'questions': 'Questions',
      'question papers': 'Question Papers',
      'invoices': 'Invoices',
      'bills': 'Bills'
    };
    return labelMap[lowerLabel] || label;
  }

  filterMenuBySubscribedFeatures(): void {
    if (!this.subscribedFeatures) return;
    const features = this.subscribedFeatures.features || {};

    const core = features.coreModules || {};
    const other = features.otherModules || {};

    const featureMenuMap: { [key: string]: boolean } = {
      library: !!core.library,
      assessments: !!core.assessment,
      finance: !!other.finance,
      customer: !!other.customer,
    };

    const originalMenuLabels = this.menuItems.map(i => i.label);
    console.log('📋 Original Menus:', originalMenuLabels);

    this.menuItems = this.menuItems.filter(item => {
      const key = item.label.toLowerCase();
      if (featureMenuMap.hasOwnProperty(key)) {
        const shouldShow = !!featureMenuMap[key];
        return shouldShow;
      }
      return true;
    });

    const filteredMenuLabels = this.menuItems.map(i => i.label);
    this.cdr.detectChanges();
  }

  ngAfterContentInit() {
    const getDecrypted = (key: string) => {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      try {
        return JSON.parse(this.util.decrypt(raw));
      } catch (error) {
        console.error(`Error parsing ${key}:`, error);
        return null;
      }
    };

    this.subscribedFeatures = getDecrypted('subscribedFeatures');
    if (this.subscribedFeatures && !this.isAdmin) {
      this.filterMenuBySubscribedFeatures();
    }
  }

}